import React, { useState } from "react";
import Modal from "../common/modal";
import { Helmet } from "react-helmet";
import { paymentService } from "../../services/payment";
import { trackEvent } from '../../utils/tracking';
import { convertAudiencesToCSV, convertTrackedKeywordsToCSV, convertBookmarksToCSV } from '../../utils/csv';

const DownloadDataModal = ({ closeModal, history, currentUser }) => {
  const [audiences, setAudiences] = useState(false);
  const [trackedKeywords, setTrackedKeywords] = useState(false);
  const [bookmarks, setBookmarks] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const hasSelection = audiences || trackedKeywords || bookmarks;

  const handleDownload = () => {
    if (!hasSelection) {
      setError("Please select at least one data type to download.");
      return;
    }

    setError(null);
    setDownloading(true);
    
    paymentService.downloadAccountData(
      {
        audiences: audiences,
        tracked_keywords: trackedKeywords,
        bookmarks: bookmarks,
      },
      (response) => {
        const dateStr = new Date().toISOString().split('T')[0];
        const filesToDownload = [];
        
        // Convert and prepare each data type for download
        if (audiences && response.data.audiences) {
          const csvContent = convertAudiencesToCSV(response.data.audiences);
          if (csvContent) {
            filesToDownload.push({
              content: csvContent,
              filename: `gummysearch-audiences-${dateStr}.csv`
            });
          }
        }
        
        if (trackedKeywords && response.data.tracked_keywords) {
          const csvContent = convertTrackedKeywordsToCSV(response.data.tracked_keywords);
          if (csvContent) {
            filesToDownload.push({
              content: csvContent,
              filename: `gummysearch-tracked-keywords-${dateStr}.csv`
            });
          }
        }
        
        if (bookmarks && response.data.bookmarks) {
          const csvContent = convertBookmarksToCSV(response.data.bookmarks);
          if (csvContent) {
            filesToDownload.push({
              content: csvContent,
              filename: `gummysearch-bookmarks-${dateStr}.csv`
            });
          }
        }
        
        // Download each file with a small delay between downloads
        filesToDownload.forEach((file, index) => {
          setTimeout(() => {
            const blob = new Blob([file.content], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, index * 200); // 200ms delay between each download
        });
        
        trackEvent("AccountDataDownloaded", {
          audiences: audiences,
          tracked_keywords: trackedKeywords,
          bookmarks: bookmarks,
        });
        
        setDownloading(false);
        closeModal();
      },
      (error, response) => {
        setError(response || "Failed to download account data");
        setDownloading(false);
      }
    );
  };

  return (
    <Modal close={closeModal} widthClass="max-w-md">
      <Helmet><title>Account | Download Data</title></Helmet>
      
      <div>
        <h3 className="text-lg font-medium leading-6 mb-4">
          Download Account Data
        </h3>

        <p className="text-sm text-gray-400 mb-4">
          Select the data you want to download. Each selected data type will be downloaded as a separate CSV file.
        </p>
        
        <div className="mb-4 space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={audiences}
              onChange={(e) => setAudiences(e.target.checked)}
              className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-600 rounded bg-gray-700"
            />
            <span className="ml-2 text-sm text-gray-300">Audiences</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={trackedKeywords}
              onChange={(e) => setTrackedKeywords(e.target.checked)}
              className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-600 rounded bg-gray-700"
            />
            <span className="ml-2 text-sm text-gray-300">Tracked Keywords</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={bookmarks}
              onChange={(e) => setBookmarks(e.target.checked)}
              className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-600 rounded bg-gray-700"
            />
            <span className="ml-2 text-sm text-gray-300">Bookmarks</span>
          </label>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-900 border border-red-700 rounded-md text-red-200 text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            onClick={closeModal}
            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white focus:outline-none"
            disabled={downloading}
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            disabled={!hasSelection || downloading}
            className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading ? 'Downloading...' : 'Download'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DownloadDataModal;

